import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";

/**
 * Invisible bot defense for public forms. Pairs with the backend shared/bot_guard:
 *  - a honeypot field ("website") that real users never see and bots tend to fill;
 *  - a short-lived form ticket fetched on mount and echoed back on submit, proving
 *    the form was actually rendered and enforcing a minimum think-time.
 *
 * Usage:
 *   const { withGuard, Honeypot } = useBotGuard();
 *   ...
 *   <form onSubmit={...}> <Honeypot /> ...fields... </form>
 *   await api.post("/endpoint", withGuard({ ...payload }));
 */
export function useBotGuard() {
  const [website, setWebsite] = useState(""); // honeypot — stays empty for humans
  const ticketRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/form-ticket`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && d.ticket) ticketRef.current = d.ticket;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const withGuard = (payload = {}) => ({
    ...payload,
    website,
    form_ticket: ticketRef.current || undefined,
  });

  // Rendered off-screen (not display:none, so headless bots that skip hidden
  // fields still see it). aria-hidden + tabIndex keep it out of the a11y tree.
  const Honeypot = () => (
    <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: 0, width: 1, height: 1, overflow: "hidden" }}>
      <label>
        Leave this field blank
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </label>
    </div>
  );

  return { withGuard, Honeypot };
}
